import { useTranslation } from 'react-i18next';
import { type ModuleDtoObject, useGetAllSimpleModulesQuery } from '@/api/generated/rest-v4-api';
import { CheckboxListFilter, type FilterOption } from '@/components/filters/checkbox-list-filter';
import { FilterGroup } from '@/components/filters/filter-group';
import { captionText } from '@/utils/caption';

const MODULES_QUERY = { includeFormList: true, filter: ['enableCalendarView:eq:true'] };

interface ModuleOption extends FilterOption {
  forms: FilterOption[];
}

// The API leaves out forms without calendar view but still returns their module, with no forms.
const toModuleOptions = (modules: ModuleDtoObject[]): ModuleOption[] =>
  modules.flatMap((module) => {
    const forms = (module.forms ?? []).flatMap((form) =>
      form.id === undefined ? [] : [{ id: form.id, label: captionText(form.caption) }]
    );
    return module.id === undefined || module.removed || module.hidden || forms.length === 0
      ? []
      : [{ id: module.id, label: captionText(module.caption), forms }];
  });

interface ModuleFilterProps {
  selectedFormIds: number[];
  onChange: (formIds: number[]) => void;
}

export const ModuleFilter = ({ selectedFormIds, onChange }: ModuleFilterProps) => {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAllSimpleModulesQuery(MODULES_QUERY);

  return (
    <FilterGroup
      title={t(($) => $.module)}
      items={toModuleOptions(data?.items ?? [])}
      isLoading={isLoading}
      showAllLabel={t(($) => $.showAllModules)}
      showLessLabel={t(($) => $.showLessModules)}
    >
      {(module) => (
        <CheckboxListFilter
          key={module.id}
          label={module.label}
          options={module.forms}
          selectedIds={selectedFormIds}
          onChange={onChange}
        />
      )}
    </FilterGroup>
  );
};
